import { isPlatformBrowser } from '@angular/common';
import { Inject, NgZone, PLATFORM_ID, Renderer2, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter, Observable, of, switchMap, tap } from 'rxjs';

import { EnvironmentService, UaService } from '@ecm/consumer';
import { EcmDispatcherModule } from '@ecm/dispatcher';
import { BrandService, ElcosService, ErrorPageService, RouterService } from '@ecm/router';
import { KenobiService } from '@ecm/tracker';

const FAVICON_PATH_ORANGE = '/assets/images/favicon/orange.png';
const FAVICON_PATH_SOSH = '/assets/images/favicon/sosh.png';
export abstract class AbstractAppComponent {
    /**
     * to define menu bar brand
     * @type {boolean}
     */
    public isSoshMenu: boolean;

    /**
     * is brand resolved to display menu bar
     * @type {boolean}
     */
    public isBrandResolved = false;

    /**
     * Is Survey active
     * @type {boolean}
     */
    private surveyActive: Observable<boolean>;

    public constructor(
        @Inject(PLATFORM_ID) protected platformId: any,
        protected renderer: Renderer2,
        protected environmentService: EnvironmentService,
        protected elcosService: ElcosService,
        protected uaService: UaService,
        protected routerService: RouterService,
        private errorPageService: ErrorPageService,
        private brandService: BrandService,
    ) {}

    public isSoshFromAbstractAppComponent$ = this.routerService.isSosh$;

    public init() {
        if (isPlatformBrowser(this.platformId)) {
            this.setupAndManageFavicon();
            if (this.environmentService.getLastError()) {
                this.errorPageService.showDefaultErrorPage();
                throw this.environmentService.getLastError();
            }
        }
        this.isSoshFromAbstractAppComponent$
            .pipe(
                switchMap(() => {
                    // isSosh legacy pour setup les états non reactif legacy
                    // ca appelle abstractAppComponent -> brandService.isSosh() -> routerService.isCurrentRouteSosh(true) -> contextService.isSosh() || routerIsSoshValue
                    return this.isSosh();
                }),
            )
            .subscribe();
    }

    // @startregion: to move in  a dedicated FaviconHandlerService once assistance apps will be destroyed
    // private readonly renderer = inject(Renderer2);
    // private readonly routerService = inject(RouterService);
    /**
     * Favicon paths
     * should be const in the future FaviconHandlerService
     * @type {string}
     */
    protected faviconPathOrange = FAVICON_PATH_ORANGE;
    protected faviconPathSosh = FAVICON_PATH_SOSH;

    private readonly _favIconState = signal({
        currentFaviconPath: '',
        currentElementRendered: null,
    });

    /**
     * Creates Favicon DOM element with Orange brand and returns reference on element
     * Uses Angular Renderer to handle DOM manually
     * Because head tag is outside of ECM app scope, so DOM interpolation is not possible
     * @returns {any}
     */
    public createFaviconElement(faviconPath = FAVICON_PATH_ORANGE): any {
        const favIconElement: any = this.renderer.createElement('link');
        this.renderer.setAttribute(favIconElement, 'rel', 'icon');
        this.renderer.setAttribute(favIconElement, 'type', 'image/png');
        this.renderer.setAttribute(favIconElement, 'href', faviconPath);
        this.renderer.appendChild(document.head, favIconElement);
        if (!this._favIconState().currentElementRendered) {
            this._favIconState.update(() => ({
                currentFaviconPath: faviconPath,
                currentElementRendered: favIconElement,
            }));
        }
        return favIconElement;
    }

    public setupAndManageFavicon(): void {
        this.routerService.isSosh$.subscribe((isSosh) => {
            const newFaviconPath = isSosh ? FAVICON_PATH_SOSH : FAVICON_PATH_ORANGE;
            if (!this._favIconState().currentElementRendered) {
                this.createFaviconElement(newFaviconPath);
                return;
            }
            if (this._favIconState().currentFaviconPath === newFaviconPath) {
                return;
            }
            this.renderer.setAttribute(this._favIconState().currentElementRendered, 'href', newFaviconPath);
            this._favIconState.update(() => ({
                currentFaviconPath: newFaviconPath,
                currentElementRendered: this._favIconState().currentElementRendered,
            }));
        });
    }
    // @endregion

    /**
     * Checks if User-Agent is mobile
     * @returns {boolean}
     */
    public isMobile(): boolean {
        return this.uaService.getDeviceType() === 'mobile';
    }

    /**
     * Template proxy for isSosh rule
     * @returns {Observable<boolean>}
     */
    public isSosh(): Observable<boolean> {
        // eslint-disable-next-line deprecation/deprecation -- this call is going to be replaced by a store/selector
        return this.brandService.isSosh().pipe(
            tap((isSosh: boolean) => {
                this.isSoshMenu = isSosh;
                this.isBrandResolved = true;
            }),
        );
    }

    /**
     * Checks if survey is active
     * @returns {Observable<boolean>}
     */
    public isSurveyActive(): Observable<boolean> {
        if (isPlatformBrowser(this.platformId) && !this.surveyActive) {
            this.surveyActive = of(this.environmentService.instant('SURVEY_ACTIVE') === true);
        }
        return this.surveyActive;
    }

    /**
     * @deprecated auto management of favicon is done in FaviconHandlerService now to delete when assistance apps will be destroyed
     * Dynamic Favicon management
     * Uses opened contract to brand Favicon
     */
    protected setFavicon(): void {
        // noop deprecated
    }

    /**
     * This method declare a global function on the window object that allow using Angular router from outside the App
     * It is mainly used by Maxymiser AB Testing and should be useful for Elcos links.
     * Router and Zone are passed as parameter to avoid injecting those dependency in webview, assistance and Eclair
     * @param {NgZone} zone
     * @param {Router} router
     */
    protected addOutsideAngularRoutingHandler(zone: NgZone, router: Router): void {
        if (isPlatformBrowser(this.platformId)) {
            (<any>window).ecmNavigateByUrl = (url) => {
                zone.run(() => {
                    router.navigateByUrl(url);
                });
            };
        }
    }

    /**
     * Track Portfolio in Kenobi (CONTPF) on Navigation End
     * Uses SessionStorage to avoid resending tracking on refresh/navigation
     * @param {Router} router
     * @param {KenobiService} kenobiService
     */
    protected trackPortfolioOnNavigationEnd(router: Router, kenobiService: KenobiService): void {
        const ise2: string = (<any>window).o_idzone?.['USER_EXTERNAL_ID_HASH'];

        if (isPlatformBrowser(this.platformId) && ise2 && window.sessionStorage.getItem('ise2') !== ise2) {
            EcmDispatcherModule.sendConsoleInfo(
                'EcmComponent',
                'ISE2 is not set in sessionStorage, will track portfolio (CONTPF) in Kenobi',
            );
            window.sessionStorage.setItem('ise2', ise2);

            const subscription = router.events
                .pipe(
                    filter((event): event is NavigationEnd => event instanceof NavigationEnd),
                    filter((event: NavigationEnd) => event.url && event.url !== '/'),
                )
                .subscribe({
                    next: () => {
                        kenobiService.trackPortfolio().subscribe({
                            next: () => {
                                // tracking has been done, we don't need to track routeChanging anymore
                                subscription.unsubscribe();
                            },
                        });
                    },
                });
        }
    }
}
